/**
 * Composable para gerenciar imagens de background otimizadas usando Nuxt Image
 *
 * @example
 * ```vue
 * <script setup>
 * const { setBackgroundImageVar, removeBackgroundImageVar } = useOptimizedBackgroundImage();
 *
 * onMounted(() => {
 *   // Definir uma imagem de hero otimizada
 *   setBackgroundImageVar(
 *     '/images/hero-background.jpg',
 *     'hero-bg-url',
 *     {
 *       format: 'webp',
 *       quality: 80,
 *       width: 1920,
 *       height: 1080
 *     }
 *   );
 * });
 *
 * onUnmounted(() => {
 *   // Limpar a variável CSS quando o componente for desmontado
 *   removeBackgroundImageVar('hero-bg-url');
 * });
 * </script>
 *
 * <template>
 *   <!-- Use no Tailwind: bg-[var(--hero-bg-url)] -->
 *   <!-- Ou defina no tailwind.config.ts: 'hero-bg': 'var(--hero-bg-url)' -->
 *   <div class="bg-[var(--hero-bg-url)] bg-cover bg-center">
 *     Hero content
 *   </div>
 * </template>
 * ```
 */
export function useOptimizedBackgroundImage() {
	const getImageSrc = useImage();

	/**
	 * Gera uma URL otimizada para uso como background-image e define uma variável CSS
	 * @param imageUrl - URL original da imagem
	 * @param cssVarName - Nome da variável CSS (sem os --)
	 * @param options - Opções de otimização da imagem
	 */
	function setBackgroundImageVar(
		imageUrl: string,
		cssVarName: string,
		options: {
			format?: 'webp' | 'webp' | 'jpg' | 'png' | 'svg';
			quality?: number;
			width?: number;
			height?: number;
			fit?: 'cover' | 'contain' | 'fill' | 'inside' | 'outside';
		} = {}
	) {
		// Configurações padrão - webp oferece ~20-30% melhor compressão que WebP
		const defaultOptions = {
			format: 'webp' as const,
			quality: 90,
			fit: 'cover' as const
		};

		const finalOptions = { ...defaultOptions, ...options };

		// Gerar URL otimizada
		const optimizedUrl = getImageSrc(imageUrl, finalOptions);

		// Definir variável CSS no DOM
		if (import.meta.client && document.documentElement) {
			document.documentElement.style.setProperty(
				`--${cssVarName}`,
				`url('${optimizedUrl}')`
			);
		}

		return optimizedUrl;
	}

	/**
	 * Remove uma variável CSS de background-image
	 * @param cssVarName - Nome da variável CSS (sem os --)
	 */
	function removeBackgroundImageVar(cssVarName: string) {
		if (import.meta.client && document.documentElement) {
			document.documentElement.style.removeProperty(`--${cssVarName}`);
		}
	}

	return {
		setBackgroundImageVar,
		removeBackgroundImageVar
	};
}
